import React, { useState } from 'react';
import {
  Box, Card, Typography, TextField, MenuItem, InputAdornment,
  IconButton, Tooltip, Button, TableContainer, Paper, Table,
  TableHead, TableRow, TableCell, TableBody, TablePagination,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InvoiceUpload from './InvoiceUpload';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import { gql, useQuery } from '@apollo/client';

const GET_CERT_INFO_QUERY = gql`
query GetCertInfo($offset: Int, $limit: Int) {
  getCertInfo(offset: $offset, limit: $limit) {
    success
    data {
      vin
      request_id
      project_name
      in_progress_date
      device_model
      device_serial_number
      device_imei_number
      engine_number
      iccid_number
      customer_name
      customer_number
      customer_otp_number
      customer_address
      vehicle_model
      registration_state
      rto_name
      rto_number
      dealer_code
      dealer_name
      dealer_contact_number
      dealer_address
      gst
      pan
      payment_status
      amount
      sim_activation_status
      msisdn_1
      msisdn_2
      vehicle_type
      year_of_manufacture
      fitment_cert
      fitment_issued_on
      common_layer_certificate
      cla_issued_on
    }
    count
    error
  }
}
`;

type Certificate = {
  id: number;
  vin: string;
  request_id: string;
  project_name: string;
  in_progress_date: string;
  device_model: string;
  device_serial_number: string;
  device_imei_number: string;
  engine_number: string;
  iccid_number: string;
  customer_name: string;
  customer_number: string;
  customer_otp_number: string;
  customer_address: string;
  vehicle_model: string;
  registration_state: string;
  rto_name: string;
  rto_number: string;
  dealer_code: string;
  dealer_name: string;
  dealer_contact_number: string;
  dealer_address: string;
  gst: string;
  pan: string;
  payment_status: string;
  amount: string;
  sim_activation_status: string;
  msisdn_1: string;
  msisdn_2: string;
  vehicle_type: string;
  year_of_manufacture: number;
  fitment_cert: string;
  fitment_issued_on: string;
  common_layer_certificate: string;
  common_layer_cert_issued_date: string;
};

// Function to format epoch timestamp to a readable date string
const formatDate = (timestamp: number): string => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp * 1000);
  return date.toLocaleString();
};

const CertificateViewer = () => {
  // State for search functionality
  const [searchType, setSearchType] = useState('vin');
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedCertificate, setSelectedCertificate] = useState<Certificate | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [selectedCertificateForUpload, setSelectedCertificateForUpload] = useState<Certificate | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { loading, error, data, refetch } = useQuery(GET_CERT_INFO_QUERY, {
    variables: { offset: 0, limit: rowsPerPage },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const totalCount = data?.getCertInfo?.count ?? 0;

  const mappedTableData: Certificate[] = (data?.getCertInfo?.data ?? []).map(
    (cert: any, index: number) => ({
      id: index + 1 + page * rowsPerPage,
      vin: cert.vin,
      request_id: cert.request_id,
      project_name: cert.project_name,
      in_progress_date: cert.in_progress_date,
      device_model: cert.device_model,
      device_serial_number: cert.device_serial_number,
      device_imei_number: cert.device_imei_number,
      engine_number: cert.engine_number,
      iccid_number: cert.iccid_number,
      customer_name: cert.customer_name,
      customer_number: cert.customer_number,
      customer_otp_number: cert.customer_otp_number,
      customer_address: cert.customer_address,
      vehicle_model: cert.vehicle_model,
      registration_state: cert.registration_state,
      rto_name: cert.rto_name,
      rto_number: cert.rto_number,
      dealer_code: cert.dealer_code,
      dealer_name: cert.dealer_name,
      dealer_contact_number: cert.dealer_contact_number,
      dealer_address: cert.dealer_address,
      gst: cert.gst,
      pan: cert.pan,
      payment_status: cert.payment_status,
      amount: cert.amount,
      sim_activation_status: cert.sim_activation_status,
      msisdn_1: cert.msisdn_1,
      msisdn_2: cert.msisdn_2,
      vehicle_type: cert.vehicle_type,
      year_of_manufacture: cert.year_of_manufacture,
      fitment_cert: cert.fitment_cert,
      fitment_issued_on: cert.fitment_issued_on,
      common_layer_certificate: cert.common_layer_certificate,
      common_layer_cert_issued_date: cert.common_layer_cert_issued_date,
    })
  );

  // Define all table headers including the new column
  const tableColumns = [
    'Sr.No', 'VIN', 'Request ID', 'Project Name', 'In-Progress Date',
    'Device Model', 'Device Serial Number', 'Device IMEI Number', 'Engine Number',
    'ICCID Number', 'Customer Name', 'Customer Number', 'Customer OTP number',
    'Customer Address', 'Vehicle Model', 'Registration State', 'RTO Name', 'RTO Number',
    'Dealer Code', 'Dealer Name', 'Dealer Contact Number', 'Dealer Address',
    'GST', 'PAN', 'Payment Status', 'Amount', 'SIM Activation status', 'MSISDN1',
    'MSISDN2', 'Vehicle Type', 'Year Of Manufacture', 'Fitment Certificate',
    'Fitment Issued On', 'Common Layer certificate', 'CLA Issued On', 'Invoice Upload', 'Actions'
  ];

  // Cell styling
  const cellStyle = {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: { xs: '4px', sm: '6px' },
    fontSize: { xs: '0.75rem', sm: '0.85rem' },
    borderRight: '1px solid #e0e0e0',
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    refetch({ offset: newPage * rowsPerPage, limit: rowsPerPage });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rpp = parseInt(event.target.value, 10);
    setRowsPerPage(rpp);
    setPage(0);
    refetch({ offset: 0, limit: rpp });
  };

  // Handle export to Excel
  const handleExportToExcel = () => {
    const formattedData = filteredData.map((cert) => ({
      'vin': cert.vin || "N/A",
      'Request ID': cert.request_id || "N/A",
      'Project Name': cert.project_name || "N/A",
      'In-Progress Date': formatDate(parseInt(cert.in_progress_date)) || "N/A",
      'Device Model': cert.device_model || "N/A",
      'Device Serial Number': cert.device_serial_number || "N/A",
      'Device IMEI Number': cert.device_imei_number || "N/A",
      'Engine Number': cert.engine_number || "N/A",
      'ICCID Number': cert.iccid_number || "N/A",
      'Customer Name': cert.customer_name || "N/A",
      'Customer Number': cert.customer_number || "N/A",
      'Customer OTP number': cert.customer_otp_number || "N/A",
      'Customer Address': cert.customer_address || "N/A",
      'Vehicle Model': cert.vehicle_model || "N/A",
      'Registration State': cert.registration_state || "N/A",
      'RTO Name': cert.rto_name || "N/A",
      'RTO Number': cert.rto_number || "N/A",
      'Dealer Code': cert.dealer_code || "N/A",
      'Dealer Name': cert.dealer_name || "N/A",
      'Dealer Contact Number': cert.dealer_contact_number || "N/A",
      'Dealer Address': cert.dealer_address || "N/A",
      'GST': cert.gst || "N/A",
      'PAN': cert.pan || "N/A",
      'Payment Status': cert.payment_status || "N/A",
      'Amount': cert.amount || "N/A",
      'SIM Activation status': cert.sim_activation_status || "N/A",
      'MSISDN1': cert.msisdn_1 || "N/A",
      'MSISDN2': cert.msisdn_2 || "N/A",
      'Vehicle Type': cert.vehicle_type || "N/A",
      'Year Of Manufacture': cert.year_of_manufacture || "N/A",
      'Fitment Issued On': formatDate(parseInt(cert.fitment_issued_on)) || "N/A",
      'CLA Issued On': formatDate(parseInt(cert.common_layer_cert_issued_date)) || "N/A",
    }));
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Certificates');
    XLSX.writeFile(workbook, 'certificates.xlsx');
  };

  // Handle view details
  const handleViewDetails = (certificate: Certificate) => {
    setSelectedCertificate(certificate);
    setDetailDialogOpen(true);
  };

  // Handle button clicks for downloads - opens URL in a new tab
  const handleButtonClick = (action: string, url: string) => {
    if (url) {
      window.open(url, '_blank');
    } else {
      console.log(`No URL found for ${action}.`);
    }
  };

  // Handle invoice upload
  const handleInvoiceUploadClick = (certificate: Certificate) => {
    setSelectedCertificateForUpload(certificate);
    setInvoiceDialogOpen(true);
  };

  const handleInvoiceUpload = (file: File) => {
    console.log('Uploading invoice for certificate with VIN:', selectedCertificateForUpload?.vin);
    console.log('File to upload:', file.name);
  };

  const handleInvoiceDialogClose = () => {
    setInvoiceDialogOpen(false);
    setSelectedCertificateForUpload(null);
  };

  // Filter data based on search term
  const filteredData = mappedTableData.filter(item => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();

    if (searchType === 'vin') {
      return item.vin.toLowerCase().includes(searchLower);
    } else if (searchType === 'requestId') {
      return item.request_id.toLowerCase().includes(searchLower);
    }

    return true;
  });
  const paginatedData = filteredData;

  return (
    <Box sx={{ width: '100%', px: { xs: 0, sm: 0 } }}>
      <Typography variant="h5" fontSize={{ xs: '1.5rem', sm: '2.125rem' }} sx={{ mb: 2, borderRadius: 2 }}>
        Certificate Viewer
      </Typography>
      <Card sx={{
        width: '100%',
        maxWidth: '100vw',
        mx: 'auto',
        boxShadow: 2,
        borderRadius: 3,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: 'calc(94vh - 120px)' },
        bgcolor: '#fff',
        minHeight: 100,
      }}>
        {/* Sticky header controls */}
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: '#fff',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          boxShadow: '0 2px 8px -4px #e0e0e0',
          px: { xs: 1, sm: 2 },
          pt: 2,
          pb: 2,
          left: 0,
          right: 0,
          width: '100%',
          minWidth: 0,
          overflow: 'visible',
        }}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}
          >
            <Box
              display="flex"
              gap={1}
              alignItems="center"
              sx={{ minWidth: 0, flex: 1, maxWidth: { xs: '100%', sm: '40%' }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
            >
              <TextField
                select
                size="small"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                sx={{ minWidth: 120, background: '#f5f5f5', flex: '0 0 auto' }}
              >
                <MenuItem value="vin">Search by VIN</MenuItem>
                <MenuItem value="requestId">Search by Request ID</MenuItem>
              </TextField>
              <TextField
                size="small"
                placeholder={
                  searchType === 'vin' ? 'Enter VIN' :
                    searchType === 'requestId' ? 'Enter Request ID' : ''
                }
                variant="outlined"
                sx={{ minWidth: 120, background: '#f5f5f5', flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <IconButton sx={{ ml: 1, color: '#000', flex: '0 0 auto' }} onClick={handleExportToExcel}>
                <Tooltip title="Export to Excel" placement="top" arrow>
                  <CloudDownloadIcon />
                </Tooltip>
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Table area */}
        <Box sx={{
          flex: 1,
          minHeight: 0,
          px: { xs: 0.5, sm: 2 },
          py: 2,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                borderRadius: 2,
                width: '100%',
                minWidth: 600,
                background: '#fff',
                overflow: 'visible',
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 600, width: '100%', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#E60012' }}>
                    {tableColumns.map((header) => (
                      <TableCell
                        key={header}
                        align="center"
                        sx={{
                          backgroundColor: '#E60012',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: { xs: '0.75rem', sm: '0.85rem' },
                          whiteSpace: 'nowrap',
                          padding: { xs: '4px', sm: '6px' },
                          borderBottom: '1px solid #e0e0e0',
                          borderRight: '2px solid #e0e0e0',
                          position: 'sticky',
                          top: 0,
                          zIndex: 11,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ fontSize: '1.1rem', color: 'red', py: 4 }}>
                        Error fetching data.
                      </TableCell>
                    </TableRow>
                  ) : paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ fontSize: '1.1rem', color: '#888', py: 4 }}>
                        No certificates found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((certificate, idx) => (
                      <TableRow
                        key={certificate.id}
                        hover
                        sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f5f5' }}
                      >
                        <TableCell align="center" sx={cellStyle}>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.vin || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.request_id || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.project_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{formatDate(parseInt(certificate.in_progress_date || "N/A"))}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.device_model || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.device_serial_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.device_imei_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.engine_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.iccid_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.customer_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.customer_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.customer_otp_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.customer_address || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.vehicle_model || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.registration_state || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.rto_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.rto_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.dealer_code || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.dealer_name || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.dealer_contact_number || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.dealer_address || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.gst || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.pan || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.payment_status || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.amount || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.sim_activation_status || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.msisdn_1 || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.msisdn_2 || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.vehicle_type || "N/A"}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{certificate.year_of_manufacture || "N/A"}</TableCell>
                        {/* Fitment Certificate column with download button */}
                        <TableCell align="center" sx={cellStyle}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleButtonClick('download_fitment', certificate.fitment_cert)}
                            disabled={!certificate.fitment_cert} // <-- disable if no URL
                            sx={{
                              backgroundColor: '#E0F7E0',
                              color: '#58B80F',
                              borderRadius: '20px',
                              padding: '0 12px',
                              height: '24px',
                              minWidth: '100px',
                              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                              textTransform: 'uppercase',
                              fontWeight: 'normal',
                              '&:hover': {
                                backgroundColor: '#C8E6C9',
                              },
                              border: '1px solid #58B80F',
                            }}
                          >
                            DOWNLOAD
                          </Button>
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{formatDate(parseInt(certificate.fitment_issued_on))}</TableCell>
                        {/* Common Layer Certificate column with download button */}
                        <TableCell align="center" sx={cellStyle}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleButtonClick('download_cla', certificate.common_layer_certificate)}
                            disabled={!certificate.common_layer_certificate} // <-- disable if no URL
                            sx={{
                              backgroundColor: '#E0F7E0',
                              color: '#58B80F',
                              borderRadius: '20px',
                              padding: '0 12px',
                              height: '24px',
                              minWidth: '100px',
                              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                              textTransform: 'uppercase',
                              fontWeight: 'normal',
                              border: '1px solid #58B80F',
                              '&:hover': {
                                backgroundColor: '#C8E6C9',
                              },

                            }}
                          >
                            DOWNLOAD
                          </Button>
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{formatDate(parseInt(certificate.common_layer_cert_issued_date))}</TableCell>
                        {/* Invoice Upload button */}
                        <TableCell align="center" sx={cellStyle}>
                          <Button
                            variant="contained"
                            onClick={() => handleInvoiceUploadClick(certificate)}
                            sx={{
                              backgroundColor: '#E0F7E0',
                              color: '#58B80F',
                              borderRadius: '20px',
                              height: '24px',
                              padding: '0 12px',
                              fontSize: '12px',
                              fontWeight: 'normal',
                              textTransform: 'none',
                              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                              '&:hover': {
                                backgroundColor: '#C8E6C9',
                              },
                              border: '1px solid #58B80F',
                              minWidth: '50px',
                            }}
                          >
                            <CloudUploadIcon fontSize="small" />
                          </Button>
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(certificate)}
                            sx={{ color: '#000', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.1)' } }}
                          >
                            <Tooltip title="View Details" placement="top" arrow>
                              <VisibilityIcon fontSize="small" />
                            </Tooltip>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        {/* Pagination */}
        <Box sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 20,
          background: '#fff',
          borderTop: '1px solid #e0e0e0',
          pt: 2,
          mt: 'auto',
          overflowX: 'auto',
        }}>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 500]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            sx={{
              minWidth: '600px',
              '.MuiTablePagination-toolbar': {
                flexWrap: 'nowrap',
                justifyContent: { xs: 'center', sm: 'space-between' },
              },
            }}
          />
        </Box>
      </Card>

      {/* Certificate Details Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#E60012', color: '#fff' }}>
          <span>Certificate Details </span>
          <IconButton aria-lable="close" onClick={() => setDetailDialogOpen(false)} size="medium" sx={{ color: 'inherit', right: 10, top: 10, position: 'absolute' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          {selectedCertificate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">VIN</Typography>
                  <Typography variant="body1">{selectedCertificate.vin}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Request ID</Typography>
                  <Typography variant="body1">{selectedCertificate.request_id}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Customer Name</Typography>
                  <Typography variant="body1">{selectedCertificate.customer_name}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Customer Number</Typography>
                  <Typography variant="body1">{selectedCertificate.customer_number}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Vehicle Model</Typography>
                  <Typography variant="body1">{selectedCertificate.vehicle_model}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Registration State</Typography>
                  <Typography variant="body1">{selectedCertificate.registration_state}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">RTO Name</Typography>
                  <Typography variant="body1">{selectedCertificate.rto_name}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Dealer Name</Typography>
                  <Typography variant="body1">{selectedCertificate.dealer_name}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Dealer Contact</Typography>
                  <Typography variant="body1">{selectedCertificate.dealer_contact_number}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Payment Status</Typography>
                  <Typography variant="body1">{selectedCertificate.payment_status}</Typography>
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                  <Typography variant="body1">{selectedCertificate.amount}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Fitment Certificate</Typography>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleButtonClick('Download fitment certificate', selectedCertificate.fitment_cert)}
                    disabled={!selectedCertificate.fitment_cert}
                    sx={{
                      backgroundColor: '#E0F7E0',
                      color: '#58B80F',
                      borderRadius: '20px',
                      padding: '0 12px',
                      height: '24px',
                      minWidth: '100px',
                      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                      textTransform: 'uppercase',
                      fontWeight: 'normal',
                      '&:hover': {
                        backgroundColor: '#C8E6C9',
                      },
                      border: '1px solid #58B80F',
                    }}
                  >
                    DOWNLOAD
                  </Button>
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Fitment Issued On</Typography>
                  <Typography variant="body1">{formatDate(parseInt(selectedCertificate.fitment_issued_on))}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Common Layer Certificate</Typography>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => handleButtonClick('Download common layer certificate', selectedCertificate.common_layer_certificate)}
                    disabled={!selectedCertificate.common_layer_certificate}
                    sx={{
                      backgroundColor: '#E0F7E0',
                      color: '#58B80F',
                      borderRadius: '20px',
                      padding: '0 12px',
                      height: '24px',
                      minWidth: '100px',
                      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                      textTransform: 'uppercase',
                      fontWeight: 'normal',
                      '&:hover': {
                        backgroundColor: '#C8E6C9',
                      },
                      border: '1px solid #58B80F',
                    }}
                  >
                    DOWNLOAD
                  </Button>
                </Box>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">CLA Issued On</Typography>
                  <Typography variant="body1">{formatDate(parseInt(selectedCertificate.common_layer_cert_issued_date))}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="subtitle2" color="textSecondary">Invoice Upload</Typography>
                  <Button
                    variant="contained"
                    onClick={() => handleInvoiceUploadClick(selectedCertificate)}
                    sx={{
                      backgroundColor: '#E0F7E0',
                      color: '#58B80F',
                      borderRadius: '20px',
                      height: '24px',
                      padding: '0 12px',
                      fontSize: '12px',
                      fontWeight: 'normal',
                      textTransform: 'none',
                      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                      '&:hover': {
                        backgroundColor: '#C8E6C9',
                      },
                      border: '1px solid #58B80F',
                      minWidth: '50px',
                    }}
                  >
                    <CloudUploadIcon fontSize="small" />
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Invoice Upload Dialog */}
      <InvoiceUpload
        open={invoiceDialogOpen}
        onClose={handleInvoiceDialogClose}
        onUpload={handleInvoiceUpload}
        certificate={selectedCertificateForUpload}
      />
    </Box>
  );
};

export default CertificateViewer;